/**
 * MicrosoftCallback — Rücksprung der Microsoft-Anmeldung, weitergereicht
 * vom Portal (Post #5420, Weg B). Das Portal hängt `code` und `state`
 * unverändert an; wir prüfen den Zufallswert im `state` gegen den
 * sessionStorage dieses Tabs, lösen den Code bei der auth-api ein und
 * gehen zu `next` aus dem `state`.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AgentOsZeichen } from '../components/AgentOsZeichen'
import { consumeMicrosoftState, handleMicrosoftCallback } from '../auth'

// Je Dokument nur ein Einlöseversuch pro Code: React StrictMode ruft Effekte
// in der Entwicklung doppelt, und ein zweiter Lauf fände den schon
// verbrauchten Zufallswert nicht mehr.
const handledCodes = new Set<string>()

export function MicrosoftCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const code = searchParams.get('code')
    const rawState = searchParams.get('state')
    const msError = searchParams.get('error')
    const run = () => {
      if (!code) {
        const desc = searchParams.get('error_description')
        setError(
          msError
            ? `Microsoft hat die Anmeldung abgelehnt (${msError})${desc ? `: ${desc}` : '.'}`
            : 'Kein Anmeldecode erhalten.'
        )
        return
      }
      if (handledCodes.has(code)) return
      handledCodes.add(code)
      const ret = consumeMicrosoftState(rawState)
      if (!ret) {
        setError('Diese Anmeldung gehört nicht zu diesem Browser-Tab oder ist abgelaufen. Bitte erneut anmelden.')
        return
      }
      handleMicrosoftCallback(code)
        .then(() => navigate(ret.next, { replace: true }))
        .catch((err: Error) => setError(err.message || String(err)))
    }
    const t = setTimeout(run, 0)
    return () => clearTimeout(t)
  }, [searchParams, navigate])

  if (error) {
    return (
      <div className="login-page">
        <div className="login-card">
          <AgentOsZeichen className="login-zeichen" />
          <p className="login-error" role="alert" style={{ marginTop: 0, marginBottom: 16 }}>{error}</p>
          <button className="login-oauth-btn" onClick={() => navigate('/login', { replace: true })}>
            Zurück zur Anmeldung
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <AgentOsZeichen className="login-zeichen" />
        <p className="login-subtitle">Anmeldung wird verarbeitet …</p>
      </div>
    </div>
  )
}
